import { styled, useTheme } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import SignUpForm, {
  isSignUpAttribute,
  SignUpAttribute,
} from "../ui/SignUpForm";
import { signUp, SignUpData, useUser } from "../../dataAccess/api/user";
import { splitFullName } from "../../helpers/formatter";
import { signIn, SignInData } from "../../dataAccess/api/token";
import { useLogout } from "../../dataAccess/HTTPClient";
import { isTranslationKey, useTranslation } from "../../helpers/i18n";
import { TextButton } from "../ui/TextButton";
import { useNavigateSameTab } from "../../helpers/navigation";
import { ServerError } from "../../dataAccess/ServerError";
import { JSONAPIServerError } from "../../dataAccess/JSONAPIServerError";
import { useConfiguration } from "../../dataAccess/api/configuration";
import ProfileInfo from "../ui/ProfileInfo";
import VerifyEmailFormPopUp from "../ui/VerifyEmailFormPopUp";
import CommunityAccessPopUp from "../ui/CommunityAccessPopUp";
import LoginForm from "../ui/LoginForm";
import { SettingsConfiguration } from "../../builder/Theme";
import { LoginSettings } from "../pages/LogIn";
import { SignupSettings } from "../pages/SignUp";
import Popover from "../ui/Popover";

type Props =
  | {
      defaultAction: "signUp";
      itemSlug?: string;
      textColor?: string;
      anchorEl: HTMLButtonElement | null;
      onClose: () => void;
    }
  | {
      defaultAction?: "signIn";
      itemSlug?: never;
      textColor?: string;
      anchorEl: HTMLButtonElement | null;
      onClose: () => void;
    };

const ProfileInfoContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const ProfileActionsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const CenteredButton = styled(TextButton)(() => ({
  alignSelf: "center",
}));

export function useProfileSettings() {
  const { configuration } = useConfiguration();
  const settings = useMemo(() => {
    let loginSettings: SettingsConfiguration = {};
    let signupSettings: SettingsConfiguration = {};
    configuration?.publishedTemplate?.properties?.pages.forEach((page) => {
      switch (page?.id) {
        case "Login":
          loginSettings = page?.settings;
          break;
        case "SignUp":
          signupSettings = page?.settings;
          break;
        default:
          break;
      }
    });
    return {
      loginSettings: loginSettings as LoginSettings,
      signupSettings: signupSettings as SignupSettings,
    };
  }, [configuration]);
  return settings;
}

function ProfileAccessModal({
  textColor,
  defaultAction,
  itemSlug,
  anchorEl,
  onClose,
}: Props) {
  const router = useRouter();
  const { mocks, isEditor, messages, setCurrentMessage } =
    useContext(EditorCtx);
  const { user: realUser, loadingUser } = useUser();
  const { push: navigate } = useNavigateSameTab();
  let user = realUser;
  if (mocks && "noUserMock" in mocks) {
    user = mocks?.noUserMock;
  }
  const { configuration } = useConfiguration();
  const { loginSettings, signupSettings } = useProfileSettings();
  const { palette } = useTheme();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [serverError, setServerError] = useState<string>();
  const [signUpErrors, setSignUpErrors] = useState<
    Map<SignUpAttribute, string>
  >(new Map());
  const logout = useLogout(configuration?.isOpenMarketplace);
  const { t } = useTranslation();
  const [currentAction, setCurrentAction] = useState(defaultAction);

  const clearBackendError = useCallback((field: SignUpAttribute) => {
    setSignUpErrors((prevErrors) => {
      const newErrors = new Map(prevErrors);
      newErrors.delete(field);
      return newErrors;
    });
  }, []);

  useEffect(() => {
    setCurrentAction(defaultAction);
  }, [defaultAction]);

  const onSubmit = useCallback(
    async (data: SignInData) => {
      if (isEditor) {
        setCurrentMessage(messages.previewAlert);
        onClose();
        return;
      }
      setLoading(true);
      try {
        await signIn(data).json();
        navigate(router?.asPath);
        onClose();
      } catch (e) {
        if (e instanceof ServerError) {
          const errorResponse = e;
          setLoginError(errorResponse.getMessage());
        }
      }
      setLoading(false);
    },
    [
      isEditor,
      messages.previewAlert,
      navigate,
      router?.asPath,
      setCurrentMessage,
      onClose,
    ],
  );

  const onSignUpSubmit = useCallback(
    async (data: SignUpData) => {
      if (isEditor) {
        setCurrentMessage(messages.previewAlert);
        return;
      }

      setLoading(true);
      setSignUpErrors(new Map());
      setServerError(undefined);

      try {
        const { firstName, lastName } = splitFullName(data.fullName);
        data.firstName = firstName;
        data.lastName = lastName;
        data.productSlug = itemSlug;
        await signUp(data).json();
        navigate(router?.asPath);
        onClose();
      } catch (error) {
        if (error instanceof JSONAPIServerError) {
          const errors = error.getAllErrors();
          const newSignUpErrors = new Map<SignUpAttribute, string>();

          errors.forEach((err) => {
            const attribute = err.getAttribute();
            const code = err.getCode();
            if (
              isSignUpAttribute(attribute) &&
              typeof code === "string" &&
              isTranslationKey(code)
            ) {
              newSignUpErrors.set(attribute, t(code));
            }
          });
          setSignUpErrors(newSignUpErrors);
        } else if (error instanceof ServerError) {
          setServerError(error.getMessage());
        } else if (error instanceof Error) {
          setServerError(error.message);
        } else {
          setServerError(t("information_generic_error"));
        }
      } finally {
        setLoading(false);
      }
    },
    [
      isEditor,
      messages.previewAlert,
      setCurrentMessage,
      setLoading,
      setSignUpErrors,
      setServerError,
      itemSlug,
      navigate,
      router?.asPath,
      onClose,
      t,
    ],
  );

  const form = useMemo(
    () =>
      currentAction === "signIn" ? (
        <LoginForm
          loading={loading}
          onSubmit={onSubmit}
          serverError={loginError}
          closePopUp={onClose}
          setOpenSignUpInProfilePopup={() => setCurrentAction("signUp")}
          settings={loginSettings}
        />
      ) : (
        <SignUpForm
          loading={loading}
          onSubmit={onSignUpSubmit}
          signUpErrors={signUpErrors}
          serverError={serverError}
          closePopUp={onClose}
          setOpenLoginInProfilePopUp={() => setCurrentAction("signIn")}
          settings={signupSettings}
          clearBackendError={clearBackendError}
        />
      ),
    [
      currentAction,
      setCurrentAction,
      loading,
      loginError,
      onSignUpSubmit,
      onSubmit,
      serverError,
      signUpErrors,
      loginSettings,
      signupSettings,
      onClose,
      clearBackendError,
    ],
  );
  return (
    <Popover anchorEl={anchorEl} onClose={onClose}>
      {!user?.id && form}
      <ProfileActionsContainer>
        {user?.id && !user?.confirmed && (
          <VerifyEmailFormPopUp email={user.email} />
        )}
        {user?.confirmed && !user?.organization?.allowedInMarketplace && (
          <CommunityAccessPopUp />
        )}
        {user?.id && (
          <>
            <ProfileInfoContainer>
              <ProfileInfo
                loading={loadingUser}
                textColor={textColor}
                user={user}
              />
            </ProfileInfoContainer>
            <CenteredButton textColor={palette.text.paper} onClick={logout}>
              {t("information_logout")}
            </CenteredButton>
          </>
        )}
      </ProfileActionsContainer>
    </Popover>
  );
}

export default ProfileAccessModal;
